// Menu "ghost tap" guard (owner 2026-11-02, phone: 'I click the pause button and the menu appears so fast that I end up
// turning off the sound because it's on the same spot').
//
// A tap that opens a menu (HUD pause, a level-up / boon draft, the run-over screen ...) is still in flight when the new
// buttons appear under the finger: the touch's own click lands on whatever is there now. So for a short window after
// every screen change, clicks that come from a pointer (finger or mouse) are swallowed. Deliberate input is never held
// back: keyboard / gamepad confirms reach the buttons as synthetic `.click()`s (detail 0, no pointer type) and a tap made
// after the window works normally. A held key's auto-repeat is the one keyboard case that is blocked inside the window
// (a browser fires a click per repeat on a focused button, so holding Enter would otherwise mash a freshly opened menu).
//
// Pure logic: no DOM at module scope. `installMenuGuard` is the thin browser binding.

/** how long after a screen change pointer clicks are ignored */
export const MENU_GUARD_MS = 350;
/** a key auto-repeat this recent marks a keyboard click as a held-key repeat */
export const KEY_REPEAT_MS = 150;

/** the bits of a click event the guard looks at */
export interface ClickLike { detail: number; pointerType?: string }

export class MenuGuard {
  /** clicks from a pointer are ignored while now < until */
  until = 0;
  private repeatAt = -Infinity;

  /** a screen changed at `now` (ms, performance.now timebase): open the guard window */
  arm(now: number, ms = MENU_GUARD_MS): void { this.until = now + ms; }
  active(now: number): boolean { return now < this.until; }
  /** a keydown happened: remember auto-repeats */
  noteKey(repeat: boolean, now: number): void { if (repeat) this.repeatAt = now; }
  /** true when this click is a finger / mouse click (as opposed to a keyboard- or script-made one) */
  static fromPointer(c: ClickLike): boolean { return c.detail > 0 || !!c.pointerType; }
  /** should this click be swallowed? */
  blocks(now: number, c: ClickLike): boolean {
    if (!this.active(now)) return false;
    if (MenuGuard.fromPointer(c)) return true;
    return now - this.repeatAt < KEY_REPEAT_MS; // held-key repeat
  }
}

/** Browser binding: swallow guarded clicks before any menu handler sees them (capture phase on the document). */
export function installMenuGuard(guard: MenuGuard, doc: Document = document, clock: () => number = () => performance.now()): void {
  doc.addEventListener('keydown', (e) => guard.noteKey(e.repeat, clock()), true);
  doc.addEventListener('click', (e) => {
    const pe = e as MouseEvent & { pointerType?: string };
    if (guard.blocks(clock(), { detail: pe.detail, pointerType: pe.pointerType })) { e.stopImmediatePropagation(); e.preventDefault(); }
  }, true);
}
